import React, { useState } from 'react';
import { X, Clock, Calendar, Play, Pause, Settings, Info } from 'lucide-react';

const ScheduleModal = ({ onClose, workflow, onScheduled }) => {
  const [scheduleType, setScheduleType] = useState('once');
  const [frequency, setFrequency] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cronExpression, setCronExpression] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [isActive, setIsActive] = useState(true);
  const [parameters, setParameters] = useState({});

  const handleSchedule = () => {
    const schedule = {
      type: scheduleType,
      frequency: scheduleType === 'recurring' ? frequency : null,
      startDate,
      startTime,
      endDate: scheduleType === 'recurring' ? endDate : null,
      cronExpression: scheduleType === 'cron' ? cronExpression : null,
      timezone,
      isActive,
      parameters,
    };
    
    onScheduled(schedule);
    onClose();
  };

  const frequencyOptions = [
    { value: 'hourly', label: 'Every Hour', cron: '0 * * * *' },
    { value: 'daily', label: 'Daily', cron: '0 9 * * *' },
    { value: 'weekly', label: 'Weekly', cron: '0 9 * * 1' },
    { value: 'monthly', label: 'Monthly', cron: '0 9 1 * *' },
    { value: 'custom', label: 'Custom', cron: '' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Schedule Workflow</h2>
              <p className="text-gray-600">Configure when and how often this workflow runs</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-6">
            {/* Schedule Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Schedule Type</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-3">
                  <input
                    type="radio"
                    value="once"
                    checked={scheduleType === 'once'}
                    onChange={(e) => setScheduleType(e.target.value)}
                    className="text-blue-600"
                  />
                  <span>Run once at specific time</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="radio"
                    value="recurring"
                    checked={scheduleType === 'recurring'}
                    onChange={(e) => setScheduleType(e.target.value)}
                    className="text-blue-600"
                  />
                  <span>Recurring schedule</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="radio"
                    value="cron"
                    checked={scheduleType === 'cron'}
                    onChange={(e) => setScheduleType(e.target.value)}
                    className="text-blue-600"
                  />
                  <span>Custom cron expression</span>
                </label>
              </div>
            </div>

            {/* Start Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {scheduleType === 'once' ? 'Execution Date' : 'Start Date'}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {scheduleType === 'once' ? 'Execution Time' : 'Start Time'}
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            {/* Recurring Frequency */}
            {scheduleType === 'recurring' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {frequencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date (Optional)</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </>
            )}

            {/* Cron Expression */}
            {scheduleType === 'cron' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cron Expression</label>
                <input
                  type="text"
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  placeholder="0 9 * * 1-5"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                <div className="mt-2 text-xs text-gray-500">
                  <div className="flex items-center space-x-2">
                    <Info className="h-4 w-4" />
                    <span>Format: minute hour day month day-of-week</span>
                  </div>
                  <div className="mt-1 ml-6 space-y-1">
                    <div>• 0 9 * * * = Every day at 9:00 AM</div>
                    <div>• 0 9 * * 1-5 = Weekdays at 9:00 AM</div>
                    <div>• 0 */2 * * * = Every 2 hours</div>
                  </div>
                </div>
              </div>
            )}

            {/* Timezone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
              </select>
            </div>

            {/* Workflow Parameters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Workflow Parameters (Optional)</label>
              <textarea
                placeholder='{"inputParam": "value", "threshold": 0.8}'
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows="4"
                onChange={(e) => {
                  try {
                    setParameters(JSON.parse(e.target.value || '{}'));
                  } catch (error) {
                    // Invalid JSON, keep current parameters
                  }
                }}
              />
              <div className="text-xs text-gray-500 mt-1">
                Provide parameters in JSON format to pass to the workflow execution
              </div>
            </div>

            {/* Schedule Status */}
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="text-blue-600"
                />
                <span className="text-sm font-medium text-gray-700">Active Schedule</span>
              </label>
              <div className="text-xs text-gray-500 mt-1">
                Uncheck to create the schedule in paused state
              </div>
            </div>

            {/* Schedule Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Schedule Preview</h4>
              <div className="text-sm text-blue-800 space-y-1">
                {scheduleType === 'once' && (
                  <div>Will run once on {startDate || 'selected date'} at {startTime || 'selected time'}</div>
                )}
                {scheduleType === 'recurring' && (
                  <div>
                    Will run {frequency} starting {startDate || 'selected date'} at {startTime || 'selected time'}
                    {endDate && ` until ${endDate}`}
                  </div>
                )}
                {scheduleType === 'cron' && (
                  <div>Will run according to cron expression: {cronExpression || 'not set'}</div>
                )}
                <div>Timezone: {timezone}</div>
                <div>Status: {isActive ? 'Active' : 'Paused'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSchedule}
              disabled={!startDate || !startTime || (scheduleType === 'cron' && !cronExpression)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              <Clock className="h-4 w-4" />
              <span>Create Schedule</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;